import { useState } from 'react';
import { Alert, Button, Code, CopyButton, Group, Select, Stack, Switch, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconCopy, IconDevicesPc, IconKey, IconShieldLock } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { PageHeader } from '../../components/PageHeader';
import { SectionCard } from '../../components/SectionCard';
import { QurilmaJadvali } from '../../components/QurilmaJadvali';
import type { Qurilma, QurilmaHolat } from '../../lib/qurilma';

interface Sozlama { panelFaqatIlova: boolean; majburiyOchiq: boolean }
interface UlashTerminal { id: string; name: string; host: string; enabled: boolean; agentKey: string }

/**
 * Platforma superadmini — panel qurilmalari (spec 2026-10-15 §8.6, §16.1):
 *  - "Panel faqat SATA ilovasi orqali" switch (favqulodda o'chirish);
 *  - maktabni ulash kaliti (terminali yo'q maktab uchun yaratish);
 *  - hamma maktablar qurilmalari — maktabning BIRINCHI qurilmasi shu yerda tasdiqlanadi.
 */
export default function Qurilmalar() {
  const { t } = useTranslation();
  const [maktab, setMaktab] = useState<string | null>(null);
  const [holat, setHolat] = useState<string | null>('KUTILMOQDA');

  const { data: maktablar = [] } = useQuery({
    queryKey: ['sa-tenants-royxat'],
    queryFn: async () => (await api.get<{ id: string; name: string }[]>('/superadmin/tenants')).data,
  });
  const { data: qurilmalar = [] } = useQuery({
    queryKey: ['sa-qurilmalar', maktab, holat],
    queryFn: async () =>
      (await api.get<Qurilma[]>('/superadmin/qurilmalar', { params: { tenantId: maktab || undefined, holat: holat || undefined } })).data,
    refetchInterval: 30_000,
  });

  const maktabData = maktablar.map((m) => ({ value: m.id, label: m.name }));
  const holatData = (['KUTILMOQDA', 'TASDIQLANGAN', 'RAD_ETILGAN', 'BLOKLANGAN'] as QurilmaHolat[]).map((h) => ({ value: h, label: t(`qurilma.holat.${h}`) }));

  return (
    <Stack>
      <PageHeader title={t('nav.qurilmalar')} subtitle={t('qurilma.kartaIzoh')} />
      <PlatformaSozlamasi />
      <UlashKaliti maktabData={maktabData} />
      <SectionCard title={t('qurilma.kartaSarlavha')} icon={IconDevicesPc}>
        <Stack gap="sm">
          <Group>
            <Select placeholder={t('qurilma.maktabTanlang')} data={maktabData} value={maktab} onChange={setMaktab} clearable searchable w={260} />
            <Select placeholder={t('common.all')} data={holatData} value={holat} onChange={setHolat} clearable w={200} />
          </Group>
          {qurilmalar.length === 0 ? (
            <Alert color="gray" variant="light">{t('common.noMatch')}</Alert>
          ) : (
            <QurilmaJadvali
              qurilmalar={qurilmalar}
              huquq={{ tasdiqlay: true, bloklay: true }}
              yol={(id, a) => `/superadmin/qurilmalar/${id}/${a}`}
              queryKeys={[['sa-qurilmalar'], ['sa-qurilma-kutilmoqda'], ['sa-tenants']]}
              maktabUstuni
            />
          )}
        </Stack>
      </SectionCard>
    </Stack>
  );
}

/** "Panel faqat SATA ilovasi orqali" — doim shu yerda; o'chirish tasdiq bilan (spec Q2–Q4). */
function PlatformaSozlamasi() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['sa-sozlama'],
    queryFn: async () => (await api.get<Sozlama>('/superadmin/sozlama')).data,
  });
  const saqla = useMutation({
    mutationFn: async (v: boolean) => (await api.patch<Sozlama>('/superadmin/sozlama', { panelFaqatIlova: v })).data,
    onSuccess: (d) => {
      qc.setQueryData(['sa-sozlama'], d);
      notifications.show({ color: 'green', message: t('qurilma.bajarildi') });
    },
  });
  const ozgartir = (v: boolean) => {
    if (v) return saqla.mutate(true);
    modals.openConfirmModal({
      title: t('qurilma.tasdiqSarlavha'),
      centered: true,
      children: <Text size="sm">{t('qurilma.ochirishOgohlantirish')}</Text>,
      labels: { confirm: t('common.save'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => saqla.mutate(false),
    });
  };
  return (
    <SectionCard title={t('qurilma.sozlamaSarlavha')} icon={IconShieldLock}>
      <Stack gap="xs">
        <Switch
          label={t('qurilma.faqatIlovaSwitch')}
          checked={!!data?.panelFaqatIlova && !data?.majburiyOchiq}
          disabled={!data || data.majburiyOchiq || saqla.isPending}
          onChange={(e) => ozgartir(e.currentTarget.checked)}
        />
        {data?.majburiyOchiq && <Alert color="orange" variant="light">{t('qurilma.majburiyOchiq')}</Alert>}
      </Stack>
    </SectionCard>
  );
}

/** Maktabni ulash kaliti (§16.1): ilovaning "Ulash" oynasiga kiritiladi. */
function UlashKaliti({ maktabData }: { maktabData: { value: string; label: string }[] }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [maktab, setMaktab] = useState<string | null>(null);
  const { data, isFetching } = useQuery({
    queryKey: ['sa-ulash-kaliti', maktab],
    enabled: !!maktab,
    queryFn: async () => (await api.get<{ terminallar: UlashTerminal[] }>(`/superadmin/tenants/${maktab}/ulash-kaliti`)).data,
  });
  const yarat = useMutation({
    mutationFn: async () => (await api.post<{ terminallar: UlashTerminal[] }>(`/superadmin/tenants/${maktab}/ulash-kaliti`)).data,
    onSuccess: (d) => qc.setQueryData(['sa-ulash-kaliti', maktab], d),
  });
  return (
    <SectionCard title={t('qurilma.ulashSarlavha')} icon={IconKey}>
      <Stack gap="xs">
        <Text size="sm" c="dimmed">{t('qurilma.ulashIzoh')}</Text>
        <Select placeholder={t('qurilma.maktabTanlang')} data={maktabData} value={maktab} onChange={setMaktab} searchable w={320} />
        {maktab && !isFetching && data?.terminallar.length === 0 && (
          <Alert color="yellow" variant="light">
            <Stack gap="xs">
              <Text size="sm">{t('qurilma.kalitYoq')}</Text>
              <Button size="xs" w="fit-content" loading={yarat.isPending} onClick={() => yarat.mutate()}>{t('qurilma.kalitYarat')}</Button>
            </Stack>
          </Alert>
        )}
        {data?.terminallar.map((tr) => (
          <Group key={tr.id} gap="xs" wrap="nowrap">
            <Text size="sm" w={200} style={{ flex: 'none' }}>
              {tr.name}{!tr.host && <Text span c="dimmed"> ({t('qurilma.sozlanmagan')})</Text>}
            </Text>
            <Code style={{ flex: 1, overflowX: 'auto', whiteSpace: 'nowrap' }}>{tr.agentKey}</Code>
            <CopyButton value={tr.agentKey}>
              {({ copied, copy }) => (
                <Button size="compact-xs" variant="light" color={copied ? 'teal' : 'gray'} onClick={copy}
                  leftSection={copied ? <IconCheck size={12} /> : <IconCopy size={12} />}>
                  {copied ? '✓' : ''}
                </Button>
              )}
            </CopyButton>
          </Group>
        ))}
      </Stack>
    </SectionCard>
  );
}
